/* eslint-disable react/require-default-props */
/* eslint-disable react/no-unused-prop-types */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ReactZoomPanPinchRef,
  TransformComponent,
  TransformWrapper,
} from 'react-zoom-pan-pinch'
import { ReactComponent as FocusIcon } from '@material-design-icons/svg/round/filter_center_focus.svg'
import debounce from 'lodash.debounce'

import IconButton from 'components/common/IconButton'

import c from './ImageComparisonSpace.module.scss'

interface ImageComparisonSpaceProps {
  imageUrl: string
  sourceUrl: string
  onLoad?: () => void
  onLoadError?: () => void
}

function ImageComparisonSpace({
  imageUrl,
  sourceUrl,
  onLoad,
  onLoadError,
}: ImageComparisonSpaceProps) {
  const [isZoomed, setIsZoomed] = useState(false)
  const [isPanningDisabled, setIsPanningDisabled] = useState(false)
  const [loadedCount, setLoadedCount] = useState(0)
  const [hasError, setHasError] = useState(false)
  const [sliderPosition, setSliderPosition] = useState(50)
  // ✅ Добавлено состояние для отслеживания drag
  const [isDragging, setIsDragging] = useState(false)

  const wrapperRef = useRef<HTMLDivElement>(null)

  const { t } = useTranslation()

  // ✅ Используем useMemo вместо useCallback для debounce
  const handleWrapperRectChange = useMemo(
    () =>
      debounce(() => {
        const rect = wrapperRef.current?.getBoundingClientRect()
        // Можно использовать если нужно для каких-то расчетов
      }, 100),
    [],
  )

  useEffect(() => {
    handleWrapperRectChange()
    window.addEventListener('resize', handleWrapperRectChange)

    return () => {
      window.removeEventListener('resize', handleWrapperRectChange)
      // ✅ Очистка debounce
      handleWrapperRectChange.cancel()
    }
  }, [handleWrapperRectChange])

  // ✅ Исправлена логика слайдера - только при активном drag
  const handleSliderMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!isDragging || !wrapperRef.current) return

      const rect = wrapperRef.current.getBoundingClientRect()
      const position = ((e.clientX - rect.left) / rect.width) * 100

      setSliderPosition(Math.max(0, Math.min(100, position)))
    },
    [isDragging],
  )

  // ✅ Отдельный handler для начала drag
  const handleSliderStart = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.stopPropagation()
      setIsDragging(true)
      setIsPanningDisabled(true)
    },
    [],
  )

  const handleSliderEnd = useCallback(() => {
    setIsDragging(false)
    setIsPanningDisabled(false)
  }, [])

  const handleImageLoad = useCallback(() => {
    setLoadedCount((prev) => {
      const newCount = prev + 1
      if (newCount === 2 && onLoad) {
        onLoad()
      }
      return newCount
    })
  }, [onLoad])

  const handleImageError = useCallback(() => {
    setHasError(true)
    if (onLoadError) {
      onLoadError()
    }
  }, [onLoadError])

  // ✅ Правильная типизация
  const handleZoomChange = useCallback((ref: ReactZoomPanPinchRef) => {
    const { scale } = ref.state
    setIsZoomed(scale > 1)
  }, [])

  if (hasError) {
    return (
      <div className={c.wrapper}>
        <p className={c.error}>{t('error.imageLoadFailed')}</p>
      </div>
    )
  }

  return (
    <div ref={wrapperRef} className={c.wrapper}>
      <TransformWrapper
        initialScale={1}
        minScale={1}
        maxScale={5}
        wheel={{ step: 0.1 }}
        pinch={{ step: 5 }}
        doubleClick={{ disabled: false, mode: 'toggle' }}
        onTransformed={handleZoomChange}
        centerOnInit
        panning={{ disabled: isPanningDisabled }}
      >
        {({ resetTransform }: { resetTransform: () => void }) => (
          <>
            <TransformComponent
              wrapperClass={c.transformWrapper}
              contentClass={c.transformContent}
            >
              <div className={c.imagesContainer}>
                <div
                  className={c.sourceImageWrapper}
                  style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
                >
                  <img
                    src={imageUrl}
                    alt="Source"
                    className={c.image}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    crossOrigin="anonymous"
                  />
                </div>

                <div
                  className={c.processedImageWrapper}
                  style={{ clipPath: `inset(0 0 0 ${sliderPosition}%)` }}
                >
                  <img
                    src={sourceUrl}
                    alt="Processed"
                    className={c.image}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    crossOrigin="anonymous"
                  />
                </div>
              </div>
            </TransformComponent>

            {/* ✅ Слайдер реагирует только при isDragging */}
            <div
              className={c.sliderContainer}
              onPointerMove={isDragging ? handleSliderMove : undefined}
              onPointerUp={handleSliderEnd}
              onPointerLeave={handleSliderEnd}
            >
              <div
                className={c.slider}
                style={{ left: `${sliderPosition}%` }}
                onPointerDown={handleSliderStart}
              >
                <div className={c.sliderLine} />
                <div className={c.sliderHandle}>
                  <svg width="24" height="24" viewBox="0 0 24 24">
                    <path
                      d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"
                      fill="white"
                    />
                    <path
                      d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z"
                      fill="white"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <IconButton
              disabled={!isZoomed}
              className={c.resetButton}
              onClick={() => {
                setIsZoomed(false)
                setSliderPosition(50)
                resetTransform()
              }}
            >
              <FocusIcon style={{ fill: 'currentColor' }} />
            </IconButton>
          </>
        )}
      </TransformWrapper>
    </div>
  )
}

export default ImageComparisonSpace